import type { BarSeriesOption, LineSeriesOption } from "echarts/charts";
import type { GridComponentOption, TitleComponentOption, TooltipComponentOption } from "echarts/components";
import type { ComposeOption } from "echarts/core";
import type { Locale } from "verdi-common/i18n";
import { AxisSide, type AxisSpec, type TimeAxisPanelSpec } from "../ChartSpec";
import { formatAxisPointerTime, formatTooltipValue } from "../format";
import type { GetDisplayInfo } from "../units/use-get-display-info";
import { type AxisScale, formatTickLabel } from "./layout/axis-layout";
import {
    AXIS_LABEL_COLOR,
    CHART_FONT_FAMILY,
    CROSSHAIR_HOVER_COLOR,
    CROSSHAIR_LABEL_BACKGROUND,
    CROSSHAIR_LABEL_BORDER_PX,
    CROSSHAIR_LABEL_FONT_SIZE_PX,
    CROSSHAIR_LABEL_LINE_HEIGHT_PX,
    CROSSHAIR_LABEL_PADDING_BOTTOM_PX,
    CROSSHAIR_LABEL_PADDING_TOP_PX,
    CROSSHAIR_LABEL_PADDING_X_PX,
    CROSSHAIR_LABEL_RADIUS_PX,
    CROSSHAIR_LABEL_TEXT_COLOR,
    GRID_LINE_COLOR,
    PLOT_BASELINE_COLOR,
    SERIES_STROKE_WIDTH_PX,
    TOOLTIP_BACKGROUND,
    TOOLTIP_FONT_SIZE_PX,
    TOOLTIP_RADIUS_PX,
    TOOLTIP_ROW_HEIGHT_PX,
    X_AXIS_POINTER_LABEL_MARGIN_PX,
    X_AXIS_TICK_LABEL_FONT_SIZE_PX,
    X_AXIS_TICK_LABEL_LINE_HEIGHT_PX,
    X_AXIS_TICK_LABEL_MARGIN_PX,
    Y_AXIS_BASE_OFFSET_PX,
    Y_TICK_FONT_SIZE_PX,
} from "./layout/plot-geometry";
import type { PlotLayout } from "./layout/plot-layout";
import type { PanelChrome } from "./plan-panel-chrome";
import type { PreparedSeries } from "./prepare-series";
import { renderTooltipHtml } from "./tooltip/tooltip-html";

type ECOption = ComposeOption<
    LineSeriesOption | BarSeriesOption | TitleComponentOption | TooltipComponentOption | GridComponentOption
>;

/** Min, max and interval come from layout/axis-layout.ts, so ECharts draws the ticks the gutter was measured for. */
const buildYAxes = (
    axes: AxisSpec[],
    axisScales: Map<string, AxisScale>,
    activeLeftAxisId: string | undefined,
): ECOption["yAxis"] => {
    // All axes share one offset, so only one per side can show tick labels without overlapping.
    // Extra axes stay in the list (series still bind to them) but hide labels. Keep in sync with
    // visibleAxesForSide in plot-layout.ts.
    const firstRightAxisId = axes.find((axis) => axis.side === AxisSide.Right)?.id;

    // Only one axis draws gridlines; left and right ticks don't line up and would cross-hatch.
    const leftAxisIds = axes.filter((axis) => axis.side === AxisSide.Left).map((axis) => axis.id);
    const gridlineAxisId = leftAxisIds.find((id) => id === activeLeftAxisId) ?? leftAxisIds[0] ?? firstRightAxisId;

    return axes.map((axis) => {
        const isVisible = axis.side === AxisSide.Left ? axis.id === activeLeftAxisId : axis.id === firstRightAxisId;
        const scale = axisScales.get(axis.id);

        return {
            id: axis.id,
            type: "value",
            position: axis.side,
            offset: Y_AXIS_BASE_OFFSET_PX,
            // No scale entry means this axis has no data. Omit min/max/interval and let ECharts
            // auto-range: nothing is plotted, so exact ticks don't matter.
            ...(scale ? { min: scale.niceMin, max: scale.niceMax, interval: scale.step } : {}),
            axisLabel: {
                show: isVisible,
                color: AXIS_LABEL_COLOR,
                // fontWeight/fontSize must stay in sync with Y_TICK_FONT in ./layout/plot-geometry.ts —
                // the gutter width was measured with that font string.
                fontWeight: 400,
                fontSize: Y_TICK_FONT_SIZE_PX,
                hideOverlap: true,
                margin: 0,
                formatter: (value: number) => {
                    if (!scale) return formatTooltipValue(value);
                    return value <= scale.maxVisibleTick ? formatTickLabel(value, scale.step) : "";
                },
            },
            splitLine: {
                show: axis.id === gridlineAxisId,
                lineStyle: { color: GRID_LINE_COLOR },
            },
        };
    });
};

export const buildTimePanelOptions = (
    spec: TimeAxisPanelSpec,
    series: PreparedSeries[],
    layout: PlotLayout,
    axisScales: Map<string, AxisScale>,
    window: { startMs: number; endMs: number },
    activeLeftAxisId: string | undefined,
    getDisplayInfo: GetDisplayInfo,
    locale: Locale,
    chrome: PanelChrome,
): ECOption => {
    // The badge names a device every series shares; when it can't, each tooltip row names its own.
    const showDeviceName = chrome.deviceName === null;
    // Sensor names are never on the badge, so the tooltip shows them on the same terms as the legend.
    const showSensorClusterName = chrome.legend.sensorClusters;
    const showSensorElementName = chrome.legend.sensorElements;

    return {
        // Off so the grid snaps when switching metrics changes the gutter, instead of sliding.
        animation: false,
        // Canvas text only; the tooltip is HTML and already inherits the page's family.
        textStyle: { fontFamily: CHART_FONT_FAMILY },
        grid: {
            // Border handled by PlotChromeLayer, a DOM overlay in panel.tsx.
            show: false,
            // We set exact pixel gutters from the shared width-group grant; containLabel would
            // re-fit the grid around whatever labels ECharts measures and fight those numbers.
            containLabel: false,
            top: 0,
            left: layout.gutters.left,
            right: layout.gutters.right,
            bottom: layout.xAxisBandPx,
        },
        tooltip: {
            // showContent, not show: show:false would take the crosshair with it.
            showContent: chrome.tooltip,
            trigger: "axis",
            // echarts flips a wide tooltip to the left of the cursor and doesn't check the panel's
            // left edge; confine clamps it back inside.
            confine: true,
            backgroundColor: TOOLTIP_BACKGROUND,
            borderColor: PLOT_BASELINE_COLOR,
            borderWidth: 1,
            borderRadius: TOOLTIP_RADIUS_PX,
            textStyle: { lineHeight: TOOLTIP_ROW_HEIGHT_PX, fontSize: TOOLTIP_FONT_SIZE_PX },
            padding: 8,
            formatter: (params) =>
                Array.isArray(params)
                    ? renderTooltipHtml(params, {
                          series,
                          getDisplayInfo,
                          showDeviceName,
                          showSensorClusterName,
                          showSensorElementName,
                          locale,
                      })
                    : "",

            // The vertical line that follows the cursor, with the hovered time on a pill below the
            // baseline. The pill covers the tick labels rather than sitting clear of them.
            axisPointer: {
                type: "line",
                label: {
                    show: true,
                    color: CROSSHAIR_LABEL_TEXT_COLOR,
                    backgroundColor: CROSSHAIR_LABEL_BACKGROUND,
                    // Border matches the fill: it counts toward the painted height either way.
                    borderColor: CROSSHAIR_LABEL_BACKGROUND,
                    borderWidth: CROSSHAIR_LABEL_BORDER_PX,
                    padding: [
                        CROSSHAIR_LABEL_PADDING_TOP_PX,
                        CROSSHAIR_LABEL_PADDING_X_PX,
                        CROSSHAIR_LABEL_PADDING_BOTTOM_PX,
                        CROSSHAIR_LABEL_PADDING_X_PX,
                    ],
                    fontSize: CROSSHAIR_LABEL_FONT_SIZE_PX,
                    lineHeight: CROSSHAIR_LABEL_LINE_HEIGHT_PX,
                    fontWeight: 500,
                    borderRadius: CROSSHAIR_LABEL_RADIUS_PX,
                },
                lineStyle: { color: CROSSHAIR_HOVER_COLOR, width: 1, type: [3, 3] },
            },
        },
        xAxis: {
            type: "time",
            // The x-axis should show the user's desired time range - not whatever the actual set of
            // downloaded data starts and ends at.
            min: window.startMs,
            max: window.endMs,
            tooltip: {
                show: false,
            },
            axisLine: {
                show: false,
            },
            axisPointer: {
                label: {
                    margin: X_AXIS_POINTER_LABEL_MARGIN_PX,
                    formatter: (params) => formatAxisPointerTime(params.value as number | string | Date),
                },
            },
            axisLabel: {
                color: AXIS_LABEL_COLOR,
                fontSize: X_AXIS_TICK_LABEL_FONT_SIZE_PX,
                lineHeight: X_AXIS_TICK_LABEL_LINE_HEIGHT_PX,
                margin: X_AXIS_TICK_LABEL_MARGIN_PX,
                showMaxLabel: false,
                showMinLabel: false,
                hideOverlap: true,

                formatter: {
                    year: "{yyyy}",
                    month: "{MMMM}",
                    day: "{MMM} {d}",
                    hour: "{h}:{mm}{a}",
                    minute: "{h}:{mm}{a}",
                },
            },
            axisTick: {
                show: false,
            },
            splitLine: {
                show: true,
                showMinLine: false,
                showMaxLine: false,
                lineStyle: {
                    color: GRID_LINE_COLOR,
                },
            },
        },
        yAxis: buildYAxes(spec.axes, axisScales, activeLeftAxisId),
        // Bars first: ECharts draws series bottom-to-top, so a bar drawn after a line would cover it.
        series: [
            ...series.filter((s) => s.seriesType === "bar").map((s) => buildSeriesOption(s, spec)),
            ...series.filter((s) => s.seriesType === "line").map((s) => buildSeriesOption(s, spec)),
        ],
    };
};

function buildSeriesOption(s: PreparedSeries, spec: TimeAxisPanelSpec): LineSeriesOption | BarSeriesOption {
    const entry = spec.streams.find((stream) => stream.streamId === s.streamId);
    const axis = spec.axes.find((a) => a.id === entry?.axisId);

    const base = {
        id: String(s.streamId),
        yAxisId: axis?.id,
        data: s.points,
        color: s.color,
        // HoverValues marks every series; echarts' emphasis marks only the nearest.
        emphasis: { disabled: true },
    };

    if (s.seriesType === "bar") {
        return { ...base, type: "bar" };
    }

    return {
        ...base,
        type: "line",
        showSymbol: false,
        connectNulls: false,
        lineStyle: { width: SERIES_STROKE_WIDTH_PX },
    };
}
